import { GoogleMap, MarkerF } from '@react-google-maps/api'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle
} from 'react'

import useGoogleMapsScript from '@/hooks/useGoogleMapsScript'

interface MapProps {
  value: google.maps.LatLngLiteral | null
  center?: google.maps.LatLngLiteral
  onMarkerPositionChange: (position: google.maps.LatLngLiteral) => void
}

const mapContainerStyle: React.CSSProperties = {
  width: '100%',
  height: '260px'
}

export interface MapRef {
  setMarkerPosition: (position: google.maps.LatLngLiteral) => void
}

const GoogleMapsComponent = forwardRef<MapRef, MapProps>(
  ({ value, center = { lat: 0, lng: 0 }, onMarkerPositionChange }, ref) => {
    const { isLoaded } = useGoogleMapsScript()
    const [map, setMap] = React.useState<google.maps.Map | null>(null)

    useImperativeHandle(ref, () => ({
      setMarkerPosition: (position: google.maps.LatLngLiteral) => {
        onMarkerPositionChange(position)
      }
    }))

    const onLoad = useCallback((map: google.maps.Map) => {
      setMap(map)
    }, [])

    const onUnmount = useCallback(() => {
      setMap(null)
    }, [])

    const onMarkerDragEnd = useCallback(
      (e: google.maps.MapMouseEvent) => {
        const lat = e.latLng?.lat()
        const lng = e.latLng?.lng()
        if (lat !== undefined && lng !== undefined) {
          onMarkerPositionChange({ lat, lng })
        }
      },
      [onMarkerPositionChange]
    )

    useEffect(() => {
      if (map) {
        map.setCenter(center)
      }
    }, [map, center])

    const handleMapClick = useCallback((e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
    }, [])

    if (!isLoaded) return null

    return (
      <div
        onClick={handleMapClick}
        onMouseDown={handleMapClick}
        onMouseUp={handleMapClick}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={11}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            disableDefaultUI: true,
            keyboardShortcuts: false
          }}>
          {value && (
            <MarkerF
              position={value}
              draggable={true}
              onDragEnd={onMarkerDragEnd}
            />
          )}
        </GoogleMap>
      </div>
    )
  }
)

GoogleMapsComponent.displayName = 'GoogleMapsComponent'

export default GoogleMapsComponent
